import { useState, useCallback } from 'react'
import { MissingImage } from 'customizations'
import { Modal, withErrorBoundary, connected, Spinner, NoResults } from 'shared'
import { SmartButtonStub } from 'ui/smart-buttons'
import EventCardBadge from 'ui/events/EventCardBadge'
import { eventDateTime, eventDateTitle } from 'ui/events/EventHelper'

const CheckInModal = ({
  person,
  events,
  conditionalRoleCheckins,
  checkinMode,
  successMessage: initialSuccess,
  errorMessage: initialError,
  placeId,
  placeCheckinId,
  modalCloseCallback = () => {}
}) => {
  const [state, setState] = useState({
    loading: false,
    selectedEvent: null,
    successMessage: initialSuccess || '',
    errorMessage: initialError || ''
  })

  const [roleCounts, setRoleCounts] = useState(() => {
    if (!conditionalRoleCheckins || _.isEmpty(conditionalRoleCheckins)) return {}

    const base = _.mapValues(_.keyBy(conditionalRoleCheckins, 'id'), () => 0)

    if (conditionalRoleCheckins.length === 1) {
      const rc = conditionalRoleCheckins[0]
      const used = rc.used_checkins ?? 0
      const total = rc.total_checkins
      const unlimited = total == null
      const remaining = unlimited ? Infinity : total - used

      if (remaining > 0) base[rc.id] = 1
    }

    return base
  })

  const getKioskIdFromUrl = useCallback(() => {
    const hash = window.location.hash
    const match = hash.match(/kiosk\/(\d+)/)
    return match ? parseInt(match[1], 10) : null
  }, [])

  const kioskId = getKioskIdFromUrl()

  const updateCount = useCallback((id, value) => {
    setRoleCounts(prev => ({ ...prev, [id]: value }))
  }, [])

  const waitAndCloseModal = () => {
    setTimeout(() => modalCloseCallback({ success: true }), 3000)
  }

  const handleSubmit = useCallback(async () => {
    setState(prev => ({
      ...prev,
      loading: true,
      errorMessage: '',
      successMessage: ''
    }))

    try {
      if (checkinMode === 'events' && state.selectedEvent) {
        const payload = { event_id: state.selectedEvent.id, user_id: person.id }
        const res = await Backend.post('/users/checkin_event', payload)

        setState(prev => ({
          ...prev,
          loading: false,
          selectedEvent: null,
          ...(res?.success
            ? { successMessage: res.message || 'Event check-in successful!' }
            : { errorMessage: res.message || 'Event check-in failed' })
        }))

        if (res?.success) waitAndCloseModal()
      } else if (checkinMode === 'manual') {
        const roleCheckins = (conditionalRoleCheckins || [])
          .map(rc => {
            const count = Math.min(roleCounts[rc.id] || 0, rc.total_checkins ?? Infinity)
            return count > 0 ? { conditional_role_id: rc.conditional_user_role_id, count } : null
          })
          .filter(Boolean)

        if (!roleCheckins.length) {
          setState(prev => ({ ...prev, loading: false }))
          return
        }

        try {
          if (placeCheckinId) {
            const res = await Backend.put('/users/place_checkins', {
              role_checkins: roleCheckins,
              user_id: person.id,
              place_id: placeId,
              place_checkin_id: placeCheckinId,
              source: 'web'
            })

            if (res?.success) {
              setState(prev => ({
                ...prev,
                loading: false,
                successMessage: 'Community Check In Submitted ',
                placeCheckinId: null
              }))
              waitAndCloseModal()
            } else {
              setState(prev => ({
                ...prev,
                loading: false,
                errorMessage: res.errors.join(', ') || 'Community Check In Submission Failed'
              }))
            }
          } else {
            const res = await Backend.post('/smart_buttons/run/place-checkin', {
              role_checkins: roleCheckins,
              user_id: person.id,
              place_id: placeId,
              source: 'web'
            })

            if (!res.errors || res.errors.length === 0) {
              setState(prev => ({
                ...prev,
                loading: false,
                successMessage: 'Community Check In Submitted ',
                placeCheckinId: null
              }))
              waitAndCloseModal()
            } else {
              setState(prev => ({
                ...prev,
                loading: false,
                errorMessage: res.errors.join(', ') || 'Community Check In Submission Failed'
              }))
            }
          }
        } catch (e) {
          console.error('Role check-in error', e)
          setState(prev => ({
            ...prev,
            loading: false,
            errorMessage: 'Community Check In Submission Failed'
          }))
        }
      } else if (checkinMode === 'automatic_community_pass') {
        const roleCheckins = (conditionalRoleCheckins || [])
          .map(rc => {
            const count = Math.min(roleCounts[rc.id] || 0, rc.total_checkins ?? Infinity)
            return count > 0 ? { conditional_role_id: rc.conditional_user_role_id, count } : null
          })
          .filter(Boolean)

        if (!roleCheckins.length) {
          setState(prev => ({ ...prev, loading: false }))
          return
        }

        try {
          const res = await Backend.post(`/users/${person.id}/create_auto_checkin_place`, {
            place_checkin: { user_id: person.id, place_id: placeId, status: 'confirmed', source: 'web' },
            role_checkins: roleCheckins,
            kiosk_id: kioskId,
          })

          if (res?.success) {
            setState(prev => ({
              ...prev,
              loading: false,
              successMessage: 'Your Check In Was Successful!',
            }))
            waitAndCloseModal()
          } else {
            setState(prev => ({
              ...prev,
              loading: false,
              errorMessage: res.error || 'Community Check In Submission Failed'
            }))
          }
        } catch (e) {
          console.error('Role check-in error', e)
          setState(prev => ({
            ...prev,
            loading: false,
            errorMessage: 'Community Check In Submission Failed'
          }))
        }
      } else {
        setState(prev => ({ ...prev, loading: false }))
        waitAndCloseModal()
      }
    } catch (err) {
      console.error('Check-in error', err)
      setState(prev => ({
        ...prev,
        loading: false,
        errorMessage: 'Unexpected error Community Check In Submission'
      }))
    }
  }, [checkinMode, state.selectedEvent, roleCounts, conditionalRoleCheckins, placeId, placeCheckinId, person.id, kioskId])

  const renderConditionalRoles = () => {
    if (checkinMode !== 'manual' && checkinMode !== 'automatic_community_pass') return null
    if (_.isEmpty(conditionalRoleCheckins)) return <NoResults>No Role Checkins</NoResults>

    return conditionalRoleCheckins.map((rc, index) => {
      const roleId = rc.conditional_user_role_id
      const used = rc.used_checkins ?? 0
      const total = rc.total_checkins
      const unlimited = total == null
      const remaining = unlimited ? Infinity : total - used
      const count = roleCounts[rc.id.toString()] || 0

      return (
        <div className="row mb-2 role-item" key={rc.id || index}>
          <div className="col-6">
            {rc.role_name} {unlimited ? '(Unlimited)' : `(${used}/${total})`}
          </div>
          <div className="col-6 d-flex align-items-center">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm rounded-circle counter-btn p-0"
              disabled={count <= 0}
              onClick={() => updateCount(rc.id, count - 1)}
            >
              -
            </button>
            <span className="counter px-2">{count}</span>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm rounded-circle counter-btn"
              disabled={remaining <= 0 || count >= remaining}
              onClick={() => updateCount(rc.id, count + 1)}
            >
              +
            </button>
          </div>
        </div>
      )
    })
  }

  const renderEvents = () => {
    if (checkinMode !== 'events') return null
    if (!events?.length) return <NoResults>No Upcoming Events</NoResults>

    return (
      <div className="row">
        {events.map(event => (
          <div className="col-sm-6 col-lg-4 col-xl-3 mb-3" key={event.uid}>
            <div
              className={`card event ${state.selectedEvent?.id === event.id ? 'selected' : ''}`}
              onClick={() => setState(prev => ({ ...prev, selectedEvent: event }))}
              role="button"
              tabIndex={0}
              onKeyPress={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setState(prev => ({ ...prev, selectedEvent: event }))
                }
              }}
            >
              <div className="card-img-container" style={{ position: 'relative' }}>
                <img
                  className="card-img-top"
                  src={event.image_preview_url || MissingImage}
                  alt={event.name}
                  style={{ height: '150px', objectFit: 'cover', width: '100%' }}
                />
                <EventCardBadge event={event} />
              </div>
              <div className="card-meta p-2">
                <time
                  className="card-note"
                  dateTime={event.open_datetime}
                  title={eventDateTitle(event)}
                  style={{ fontSize: '12px', color: '#777' }}
                >
                  {eventDateTime(event)}
                </time>
                <h5 className="card-heading" style={{ fontSize: '16px', margin: '5px 0' }}>
                  {event.name}
                </h5>
                <div className="card-subheading" style={{ fontSize: '14px', color: '#555' }}>
                  {event.place_name}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderSubmitButton = () => {
    const hasSelection = state.selectedEvent || _.sum(_.values(roleCounts)) > 0
    if (!hasSelection) return null

    return (
      <div className="col-12 footer text-right mt-3">
        <SmartButtonStub
          text="Submit"
          buttonType="button"
          className="admin-purple inverted primary custom-font-size mb-1 mb-md-0"
          spinning={state.loading}
          click={handleSubmit}
        />
      </div>
    )
  }

  if (state.loading) return <Spinner />

  return (
    <div className="PersonModal CheckInModal custom-modal">
      <div className="p-3 modal-header">
        <div className="d-flex align-items-center">
          <div className="person-avatar-cell">
            <a className="plain-link" href={`#/people/${person?.id}`}>
              <img
                src={person?.picture || MissingImage}
                className="person-avatar rounded-circle"
                alt={person?.name || 'User'}
              />
            </a>
          </div>
          <div className="username">{person?.name}</div>
        </div>
        <Modal.CloseButton />
      </div>

      <div className="modal-body d-block">
        {state.successMessage && (
          <div className="alert alert-success" role="alert">
            {state.successMessage}
          </div>
        )}

        {state.errorMessage && (
          <div className="alert alert-danger" role="alert">
            {state.errorMessage}
          </div>
        )}

        {(checkinMode === 'manual' || checkinMode === 'automatic_community_pass') && !(state.successMessage || state.errorMessage) && (
          <>
            <h4 className="section-info-heading mb-3">Community Passes:</h4>
            {renderConditionalRoles()}
          </>
        )}

        {checkinMode === 'events' && (
          <>
            <h4 className="section-info-heading mb-3">Upcoming Events Attending:</h4>
            {renderEvents()}
          </>
        )}

        {!(state.successMessage || state.errorMessage) && renderSubmitButton()}
      </div>
    </div>
  )
}

export default withErrorBoundary(connected(CheckInModal))